import React from 'react';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

import { Divisor, Button } from 'components';
import { staticURL } from 'core/url';
import { BillingCycleChooser } from 'apps/onboarding/subscription/billingCycleChooser';
import {
  AddPaymentDetails,
  AddPaymentDetailsProps,
} from 'apps/payment/views/addPaymentDetails';
import { StartFreeTrail } from 'constants/subscription';
import { analytics } from 'core/analytics';
import { clientStore } from 'apps/client/app';
import { ProductPlan } from '../productPlan';
import { NextPaymentFreeBar } from '../nextPaymentFreeBar';

interface PlanReviewProps {
  productType: 'pro' | 'business';
  totalPrice: number;
  seats: number;
  earlyUpgrade?: boolean;
  nextPaymentDate: string;
  nextPaymentDateForReview: string;
  billingCycle: 'yearly' | 'monthly';
  componentMode: 'edit' | 'readOnly';
  annualPriceDetails?: {
    unsavedPercentage: number;
    productPriceByDiscount: number;
    originPrice: number;
  };
  subscriptionConfirmed: boolean;
  billingMonths: number;
  isStartFreeTrial?: boolean;
  submitting: boolean;
  addPaymentDetailsProps: AddPaymentDetailsProps;
  onBillingCycleChange?: (billingCycle: 'yearly' | 'monthly') => void;

  onAddSeat: () => void;
  onGetStarted: () => void;
  onStartFreeTrial: () => void;
  onConfirmChoosePlan?: () => void;
  onSwitchProductType: (productType: 'pro' | 'business') => void;
}

const productTypeText = {
  pro: 'Pro',
  business: 'Business',
};

export const PlanReview = (props: PlanReviewProps) => {
  const {
    productType,
    totalPrice,
    seats,
    billingMonths,
    annualPriceDetails,
    billingCycle,
    submitting = false,
    subscriptionConfirmed = false,
    isStartFreeTrial = false,
    componentMode,
    earlyUpgrade,
    onAddSeat,
    onSwitchProductType,
    onConfirmChoosePlan,
    onGetStarted,
    onStartFreeTrial,
    onBillingCycleChange,
    addPaymentDetailsProps,
    nextPaymentDate,
    nextPaymentDateForReview,
  } = props;

  const handleBillingCycleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onBillingCycleChange) {
      onBillingCycleChange(e.target.value as 'yearly' | 'monthly');
    }
  };

  const { unsavedPercentage, originPrice } = annualPriceDetails;

  const billingCycleText = billingCycle === 'yearly' ? 'year' : 'month';
  const switchPlanText = productType === 'business' ? 'Pro' : 'Business';
  const showAddSeat =
    productType !== 'pro' &&
    componentMode !== 'readOnly' &&
    !subscriptionConfirmed;

  let submitPaymentSuccessAction = onGetStarted;
  let submitText =
    componentMode === 'readOnly' && productType !== 'pro'
      ? 'Continue'
      : 'Get Started';

  const getConfirmButton = () => {
    if (componentMode === 'readOnly') {
      submitPaymentSuccessAction = onConfirmChoosePlan;
      if (productType === 'business') {
        submitText = earlyUpgrade ? StartFreeTrail : 'Upgrade';
        return (
          <Button
            dataTestId='reviewPlanPage-upgrade-button'
            text={earlyUpgrade ? StartFreeTrail : 'Upgrade'}
            icon='arrow_right'
            iconPosition='right'
            loading={submitting}
            variant='tertiary'
            onClick={() => {
              onConfirmChoosePlan();
            }}
          />
        );
      }

      if (productType === 'pro') {
        submitText = earlyUpgrade ? StartFreeTrail : 'Downgrade';

        return (
          <Button
            dataTestId='reviewPlanPage-downgrade-button'
            text={earlyUpgrade ? StartFreeTrail : 'Downgrade'}
            icon='arrow_right'
            iconPosition='right'
            loading={submitting}
            variant='tertiary'
            onClick={() => {
              onConfirmChoosePlan();
            }}
          />
        );
      }
    }

    submitPaymentSuccessAction = onStartFreeTrial;
    submitText = 'Start Free Trial';
    return (
      <Button
        dataTestId='reviewPlanPage-startFreeTrial-button'
        text='Start Free Trial'
        icon='arrow_right'
        iconPosition='right'
        loading={submitting}
        variant='tertiary'
        onClick={() => {
          onStartFreeTrial();
        }}
      />
    );
  };

  const showAlertMsg = (
    <div className='flex items-start'>
      <AiOutlineExclamationCircle className='mt-1' />
      <p className='ml-1'>
        {isStartFreeTrial
          ? `Cancel any time for free, before your first payment on ${nextPaymentDateForReview}.`
          : `Once you change plan, the next payment will take place on ${nextPaymentDateForReview}.`}
      </p>
    </div>
  );

  const startButton = (
    <div className='flex justify-center mt-4'>
      {subscriptionConfirmed ? (
        <Button
          dataTestId='reviewPlanPage-getStarted-button'
          text={
            componentMode === 'readOnly' && productType !== 'pro'
              ? 'Continue'
              : 'Get Started'
          }
          icon='check'
          iconPosition='right'
          loading={submitting}
          variant='tertiary'
          onClick={() => {
            // add billling cycles data i.e. monthly/annual?
            analytics.sendAdwordsConversion(
              'freeTrial',
              productType,
              totalPrice,
            );
            if (onGetStarted) {
              onGetStarted();
            }
          }}
        />
      ) : (
        getConfirmButton()
      )}
    </div>
  );

  const changePrice = (
    <>
      {billingCycle === 'yearly' ? (
        <div>
          <span
            className='text-xs line-through'
            data-testid='reviewPlanPage-originPrice-text'
          >
            €{originPrice}
          </span>
          <span
            className='text-xs ml-2'
            data-testid='reviewPlanPage-totalPriceDetail-text'
          >
            {`${seats} editors x ${billingMonths} months`}
          </span>
        </div>
      ) : (
        <span
          className='text-xs'
          data-testid='reviewPlanPage-totalPriceDetail-text'
        >
          {`${seats} editors x ${billingMonths} months`}
        </span>
      )}

      {billingCycle === 'yearly' && unsavedPercentage && (
        <span
          className='text-12 text-blue-500 bg-blue-100 rounded-full h-5 px-2'
          data-testid='reviewPlanPage-discount-text'
        >
          {unsavedPercentage}% off
        </span>
      )}
    </>
  );

  const renderPaymentFormOrStartButton = () => {
    if (!isStartFreeTrial && addPaymentDetailsProps.creditCards.length === 0) {
      return (
        <AddPaymentDetails
          {...addPaymentDetailsProps}
          showAddPayment={componentMode === 'edit' || productType === 'pro'}
          startButton={startButton}
          loading={submitting}
          isStartFreeTrial={isStartFreeTrial}
          successAction={() => {
            submitPaymentSuccessAction();
          }}
          submitText={submitText}
        />
      );
    }

    if (subscriptionConfirmed || isStartFreeTrial) {
      return startButton;
    }

    return (
      <AddPaymentDetails
        {...addPaymentDetailsProps}
        showAddPayment={componentMode === 'edit' || productType === 'pro'}
        startButton={startButton}
        loading={submitting}
        isStartFreeTrial={isStartFreeTrial}
        successAction={() => {
          submitPaymentSuccessAction();
        }}
        submitText={submitText}
      />
    );
  };

  return (
    <div className=' px-8'>
      {subscriptionConfirmed ? (
        <div>
          <div className='flex'>
            <h1
              className={`text-black whitespace-nowrap ${
                clientStore.isMobile ? 'text-2xl' : 'text-3xl'
              }`}
            >
              {isStartFreeTrial
                ? 'Your Free Trial Has Started'
                : 'Your Plan Has Changed'}
            </h1>
            <img
              width={24}
              className='ml-2'
              src={staticURL('assets/icons/check_with_circle.svg')}
            />
          </div>
          {showAlertMsg}
        </div>
      ) : (
        <>
          <h1 className='text-black whitespace-nowrap'>
            {isStartFreeTrial
              ? 'Review Your Free Trial'
              : 'Review Your New Plan'}
          </h1>
          {showAlertMsg}
        </>
      )}
      <div className='flex justify-between items-center gap-2 md:gap-0'>
        <h1 className='text-black text-18'>
          Wikifactory {productTypeText[productType]} Plan
        </h1>
        {!subscriptionConfirmed && componentMode !== 'readOnly' && (
          <span className='flex-center gap-1'>
            Switch to{' '}
            <span
              data-testid='reviewPlanPage-switchPlan-button'
              className='cursor-pointer font-extrabold'
              onClick={() => {
                onSwitchProductType(
                  productType === 'business' ? 'pro' : 'business',
                );
              }}
            >
              {switchPlanText}
            </span>
          </span>
        )}
      </div>
      <ProductPlan productType={productType} />
      <Divisor mt={4} />
      <BillingCycleChooser
        isMobile={clientStore.isMobile}
        billingCycle={billingCycle}
        editable={!subscriptionConfirmed}
        disabled={submitting}
        onChange={handleBillingCycleChange}
      />
      <Divisor />
      <div className='flex justify-between my-4'>
        <h2
          className='text-black'
          data-testid='reviewPlanPage-seatsNumber-text'
        >
          {seats} Seats
        </h2>
        {showAddSeat && (
          <span
            className={`text-sm ${
              submitting
                ? 'text-gray-300 cursor-not-allowed opacity-100'
                : 'cursor-pointer text-blue-500'
            }`}
            data-testid='reviewPlanPage-addSeat-button'
            onClick={() => {
              if (onAddSeat && !submitting) {
                onAddSeat();
              }
            }}
          >
            +Add Seats
          </span>
        )}
      </div>
      <Divisor />
      <div className='flex justify-between mt-4'>
        <h2 className='text-black'>Total Price</h2>
        {isStartFreeTrial ? (
          <h2 data-testid='reviewPlanPage-freeTrailTotalPrice-text'>€0</h2>
        ) : (
          <h2 data-testid='reviewPlanPage-totalPrice-text'>
            €{totalPrice} / {billingCycleText}
          </h2>
        )}
      </div>
      {isStartFreeTrial ? (
        <NextPaymentFreeBar
          totalPrice={totalPrice}
          nextPaymentDate={nextPaymentDate}
          className='mb-4'
          totalPriceTestId='reviewPlanPage-totalPrice-text'
        />
      ) : (
        <div className='flex justify-between mt-2 mb-4'>{changePrice}</div>
      )}
      <Divisor />

      {renderPaymentFormOrStartButton()}
    </div>
  );
};
